"use client";

import { KiteVersion, KiteVersionRequestDTO } from "@/common/model/KiteVersion";
import { KiteVersionService } from "@/services/KiteVersionService";
import GearItemsWithDialog from "./GearItemsWithDialog";

interface KiteVersionsProps {
  initialSearch?: string;
  onSearchChange?: (search: string) => void;
  initialPage?: number;
  onPageChange?: (page: number) => void;
  initialLimit?: number;
  onLimitChange?: (limit: number) => void;
}

const KiteVersions = ({
  initialSearch = "",
  onSearchChange,
  initialPage = 1,
  onPageChange,
  initialLimit = 5,
  onLimitChange,
}: KiteVersionsProps = {}) => {
  // Define columns for the versions table
  const columns = [
    {
      field: "version",
      header: "Version",
      sortable: false,
    },
    {
      field: "isyearversion",
      header: "Year Version",
      sortable: false,
      render: (item: KiteVersion) => (
        <span>{item.isyearversion ? "Yes" : "No"}</span>
      ),
    },
  ];

  // Define form fields for adding/editing versions
  const formFields = [
    {
      name: "version",
      label: "Version Name",
      type: "text" as const,
      required: true,
    },
    {
      name: "isyearversion",
      label: "Year Version",
      type: "select" as const,
      required: true,
      options: [
        { value: "true", label: "Yes" },
        { value: "false", label: "No" },
      ],
    },
  ];

  // Function to create RequestDTO from form data
  const createVersionRequestDto = (
    formData: Record<string, any>
  ): KiteVersionRequestDTO => {
    return {
      version: formData.version,
      isyearversion: formData.isyearversion === "true", // Convert string to boolean
    };
  };

  // Create a custom service that handles the specific error for versions in use
  const versionService = {
    getItems: KiteVersionService.getVersions,
    createItem: KiteVersionService.createVersion,
    updateItem: KiteVersionService.updateVersion,
    // Override the deleteItem method to intercept the specific error
    deleteItem: async (id: number) => {
      try {
        const success = await KiteVersionService.deleteVersion(id);
        return success;
      } catch (error: any) {
        // Check if this is a "Version is in use" error
        if (
          error?.response?.data?.error ===
          "Version is in use and cannot be deleted"
        ) {
          // Throw a more user-friendly error that will be displayed to the user
          throw new Error(
            "This version cannot be deleted because it is in use by one or more users."
          );
        }
        // For other errors, just let them propagate
        throw error;
      }
    },
  };

  return (
    <GearItemsWithDialog<KiteVersion, KiteVersionRequestDTO>
      title="Manage Versions"
      service={versionService}
      columns={columns}
      formFields={formFields}
      itemTypeNameSingular="Version"
      itemTypeNamePlural="versions"
      requestDtoFactory={createVersionRequestDto}
      initialSearch={initialSearch}
      onSearchChange={onSearchChange}
      initialPage={initialPage}
      onPageChange={onPageChange}
      initialLimit={initialLimit}
      onLimitChange={onLimitChange}
    />
  );
};

export default KiteVersions;
